// src/components/partners/DistributorDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { Edit, DollarSign, ArrowLeft, User, Mail, Phone, MapPin, FileText, Hash, Calendar, Clock } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { getDistributorById } from '../../api/partner';

const DistributorDetails = ({ distributorId, onEdit, onManagePrices, onBack }) => {
    const { isAdmin, isManager } = useAuth();
    const [distributor, setDistributor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const canModify = isAdmin || isManager;

    useEffect(() => {
        const fetchDistributorDetails = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getDistributorById(distributorId);
                setDistributor(data);
            } catch (err) {
                setError('Impossible de charger les détails du distributeur. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (distributorId) {
            fetchDistributorDetails();
        }
    }, [distributorId]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (loading) {
        return (
            <Card className="shadow-sm">
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement des détails du distributeur...</p>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="danger">{error}</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (!distributor) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <Alert variant="warning">Aucun distributeur trouvé.</Alert>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm">
            {/* En-tête : empile titre puis boutons sous sm, ligne dès sm */}
            <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                <h5 className="mb-0 d-flex align-items-center">
                    <User size={20} className="me-2 text-primary" />
                    Détails du distributeur
                </h5>
                {canModify && (
                    <div className="d-flex gap-2 flex-wrap">
                        <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => onManagePrices(distributorId)}
                        >
                            <DollarSign size={16} className="me-1" />
                            Gérer les prix
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onEdit(distributorId)}
                        >
                            <Edit size={16} className="me-1" />
                            Modifier
                        </Button>
                    </div>
                )}
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="mb-4">
                            <h4>{distributor.name}</h4>
                            <div className="mt-3">
                                {distributor.contractNumber && (
                                    <p className="text-muted mb-2 d-flex align-items-center">
                                        <Hash size={16} className="me-2" />
                                        Contrat: {distributor.contractNumber}
                                    </p>
                                )}
                                {distributor.email && (
                                    <p className="text-muted mb-2 d-flex align-items-center text-break">
                                        <Mail size={16} className="me-2 flex-shrink-0" />
                                        {distributor.email}
                                    </p>
                                )}
                                {distributor.phone && (
                                    <p className="text-muted mb-2 d-flex align-items-center">
                                        <Phone size={16} className="me-2" />
                                        {distributor.phone}
                                    </p>
                                )}
                            </div>
                        </div>

                        {distributor.address && (
                            <div className="mb-4">
                                <h6 className="mb-2 d-flex align-items-center">
                                    <MapPin size={16} className="me-2" />
                                    Adresse
                                </h6>
                                <div className="bg-light p-3 rounded">
                                    {distributor.address.split('\n').map((line, index) => (
                                        <p key={index} className={index !== 0 ? 'mt-1 mb-0' : 'mb-0'}>
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-4">
                            <div className="d-flex flex-wrap gap-2">
                                <Badge bg={distributor.vatApplicable ? 'success' : 'secondary'} className="p-2">
                                    {distributor.vatApplicable ? 'TVA applicable' : 'Non soumis à TVA'}
                                </Badge>

                                {distributor.creditLimit && (
                                    <Badge bg="info" className="p-2">
                                        Limite de crédit: {distributor.creditLimit.toFixed(2)}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </Col>

                    <Col md={6}>
                        <Table bordered responsive size="sm" className="mt-md-0 mt-3">
                            <tbody>
                                <tr>
                                    <td className="bg-light" width="40%">
                                        <User size={14} className="me-1" /> Type de partenaire
                                    </td>
                                    <td>
                                        <Badge bg="primary">Distributeur</Badge>
                                    </td>
                                </tr>
                                {distributor.user && (
                                    <tr>
                                        <td className="bg-light">
                                            <Mail size={14} className="me-1" /> Compte utilisateur
                                        </td>
                                        <td className="text-break">{distributor.user.username || distributor.user.email}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="bg-light">
                                        <Calendar size={14} className="me-1" /> Créé le
                                    </td>
                                    <td>{formatDate(distributor.createdAt)}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light">
                                        <Clock size={14} className="me-1" /> Dernière mise à jour
                                    </td>
                                    <td>{formatDate(distributor.updatedAt)}</td>
                                </tr>
                            </tbody>
                        </Table>

                        {distributor.prices && distributor.prices.length > 0 && (
                            <div className="mt-4">
                                <h6 className="mb-2">Prix spécifiques ({distributor.prices.length})</h6>
                                <div className="bg-light p-3 rounded">
                                    <Table responsive size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Produit</th>
                                                <th>Prix spécifique</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {distributor.prices.slice(0, 5).map((price, index) => (
                                                <tr key={index}>
                                                    <td>{price.product.name}</td>
                                                    <td>{parseFloat(price.price).toFixed()} F cfa</td>
                                                </tr>
                                            ))}
                                            {distributor.prices.length > 5 && (
                                                <tr>
                                                    <td colSpan="2" className="text-center">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => onManagePrices(distributorId)}
                                                        >
                                                            Voir tous les prix ({distributor.prices.length})
                                                        </Button>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>

                <div className="d-flex justify-content-start mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        <ArrowLeft size={16} className="me-1" />
                        Retour à la liste
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default DistributorDetails;